import { t } from '../../shared/i18n.js';
import { el, clear } from './dom.js';
import { icon } from './icons.js';
import { button } from './components.js';
import { BLIP, ROUTE_COLOUR } from './minimap.js';
import { TILE } from '../../shared/geo.js';

// what each kind of place is called in the search results
const KIND = {
  town: t('Town'), village: t('Village'), quarter: t('Quarter'), market: t('Market'), school: t('School'), college: t('College'), university: t('University'),
  hospital: t('Hospital'), clinic: t('Clinic'), worship: t('Place of worship'), bank: t('Bank'), fuel: t('Fuel station'), bus: t('Motor park'), hotel: t('Hotel'),
  airport: t('Airport'), stadium: t('Stadium'), sight: t('Place to see'), police: t('Police'), food: t('Food'), bar: 'Bar', shop: 'Shop', office: 'Office',
  townhall: 'Council', court: 'Court', post: 'Post office', library: 'Library', pharmacy: 'Pharmacy', peak: 'Hill', taxi: 'Taxi rank', building: 'Building',
};
// names drawn on the map, the more important from farther out: [kind, zoom at which it appears]
const LABELS = [['town', 0], ['village', 0.09], ['quarter', 0.16], ['university', 0.12], ['airport', 0], ['market', 0.2], ['hospital', 0.25], ['bus', 0.2], ['sight', 0.25], ['stadium', 0.3], ['college', 0.5], ['school', 0.6], ['worship', 0.7], ['fuel', 0.7], ['bank', 0.8], ['hotel', 0.8], ['police', 0.6], ['townhall', 0.5], ['pharmacy', 0.9], ['shop', 1.2], ['bar', 1.2], ['food', 1.0], ['office', 1.4], ['library', 0.9], ['court', 0.7], ['post', 0.9], ['taxi', 0.8], ['clinic', 0.6], ['peak', 0.3], ['building', 1.6]];
const SHOWN = new Map(LABELS);

// The full-screen map of the whole region: drag to move it, scroll or pinch to
// zoom, type to find a place, click anywhere to be shown the way there.
export class BigMap {
  // map: the loaded region. onGoal({ n, x, z }): the player picked somewhere to go.
  constructor(root, map, onGoal, onClear) {
    this.map = map;
    this.onGoal = onGoal;
    this.onClear = onClear;
    this.route = null;
    this.minimap = null; // set by the game: its pre-drawn tiles serve the close-up view
    this.canvas = el('canvas', { 'aria-label': `Map of the ${map.meta.name}` });
    this.search = el('input', { type: 'search', placeholder: 'Find a place: a market, a school, a village…', 'aria-label': t('Find a place'), autocomplete: 'off' });
    this.results = el('ul', { role: 'listbox', 'aria-label': t('Places found') });
    this.node = el(
      'div',
      { id: 'bigmap', hidden: true },
      this.canvas,
      el('div.find', null, this.search, this.results),
      el('button.close', { type: 'button', 'aria-label': t('Close the map'), onclick: () => this.close() }, '×'),
      (this.legendButton = button(t('Legend'), { kind: 'secondary', size: 'sm', icon: 'legend', extra: 'legend-toggle', onclick: () => this.toggleLegend() })),
      (this.clearButton = button(t('Clear the route'), { kind: 'secondary', size: 'sm', icon: 'close', extra: 'route-clear', onclick: () => { this.route = null; if (this.onClear) this.onClear(); this.draw(); } })),
      (this.legend = el('div.legend.card', { hidden: true })),
      (this.pickCard = el('div.pick-card.card', { hidden: true })),
      el('p.tip', null, t('Drag to move · scroll or pinch to zoom · tap a place to go there'))
    );
    this.buildLegend();
    root.append(this.node);
    this.ctx = this.canvas.getContext('2d');
    this.picture = null; // loaded the first time the map is opened
    this.cx = 0; // world point at the middle of the view
    this.cz = 0;
    this.zoom = 0.1; // screen pixels per metre
    this.me = { x: 0, z: 0, yaw: 0 };
    this.marks = [];
    this.onClose = null;

    this.search.addEventListener('input', () => this.find());
    this.search.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this.found && this.found[0]) this.pick(this.found[0]);
      if (e.key === 'Escape' || e.key === 'Tab') {
        e.preventDefault();
        this.close();
      }
      e.stopPropagation();
    });

    // dragging moves the map; a press that does not move is a click
    let drag = null;
    const pinch = new Map();
    this.canvas.addEventListener('pointerdown', (e) => {
      this.canvas.setPointerCapture(e.pointerId);
      pinch.set(e.pointerId, { x: e.clientX, y: e.clientY });
      drag = { x: e.clientX, y: e.clientY, moved: 0 };
    });
    this.canvas.addEventListener('pointermove', (e) => {
      const p = pinch.get(e.pointerId);
      if (!p || !drag) return;
      if (pinch.size === 2) {
        const [a, b] = [...pinch.values()];
        const before = Math.hypot(a.x - b.x, a.y - b.y);
        p.x = e.clientX;
        p.y = e.clientY;
        const after = Math.hypot(a.x - b.x, a.y - b.y);
        if (before > 0) this.zoomBy(after / before, (a.x + b.x) / 2, (a.y + b.y) / 2);
        drag.moved = 99;
        return;
      }
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      p.x = e.clientX;
      p.y = e.clientY;
      drag.moved += Math.abs(dx) + Math.abs(dy);
      this.cx -= dx / this.zoom;
      this.cz -= dy / this.zoom;
      this.draw();
    });
    const up = (e) => {
      pinch.delete(e.pointerId);
      if (drag && drag.moved < 6 && e.type === 'pointerup') {
        const w = this.toWorld(e.clientX, e.clientY);
        this.offer(this.nearestNamed(w.x, w.z) || { n: t('the place you marked'), x: Math.round(w.x), z: Math.round(w.z) });
      }
      if (pinch.size === 0) drag = null;
    };
    this.canvas.addEventListener('pointerup', up);
    this.canvas.addEventListener('pointercancel', up);
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.zoomBy(Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY);
    }, { passive: false });
    window.addEventListener('resize', () => {
      if (!this.node.hidden) this.draw();
    });
  }

  get open() {
    return !this.node.hidden;
  }

  // me: { x, z, yaw } of the player. marks: [{ x, z, color }] places to go.
  show(me, marks) {
    this.me = me;
    this.marks = marks;
    this.node.hidden = false;
    this.cx = me.x;
    this.cz = me.z;
    this.zoom = 0.12;
    this.search.value = '';
    this.pickCard.hidden = true;
    this.clearButton.hidden = !this.route;
    this.find();
    if (!this.picture && this.map.meta.overview) {
      const img = new Image();
      img.onload = () => {
        this.picture = img;
        this.draw();
      };
      img.src = `map/${this.map.regionId}/overview.png?v=${this.map.meta.version || this.map.meta.built}`;
    }
    this.draw();
    this.search.focus();
  }

  close() {
    this.node.hidden = true;
    this.search.blur();
    if (this.onClose) this.onClose();
  }

  toWorld(sx, sy) {
    const r = this.canvas.getBoundingClientRect();
    return { x: this.cx + (sx - r.left - r.width / 2) / this.zoom, z: this.cz + (sy - r.top - r.height / 2) / this.zoom };
  }

  zoomBy(f, sx, sy) {
    const before = this.toWorld(sx, sy);
    this.zoom = Math.max(0.012, Math.min(8, this.zoom * f));
    const after = this.toWorld(sx, sy);
    this.cx += before.x - after.x;
    this.cz += before.z - after.z;
    this.draw();
  }

  // A named place under a click, if the click was close to one that is drawn.
  nearestNamed(x, z) {
    let best = null;
    let bestD = (16 / this.zoom) ** 2; // within sixteen screen pixels
    for (const p of this.map.places) {
      const at = SHOWN.get(p.k);
      if (at === undefined || this.zoom < at) continue;
      const d = (p.x - x) ** 2 + (p.z - z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best;
  }

  find() {
    const q = this.search.value.trim().toLowerCase();
    clear(this.results);
    this.found = [];
    if (q.length < 2) return;
    // A place matches by its name or by what it is ("market" finds every market).
    // Names come first, then the kinds of place people look for most, then the nearest.
    const matches = [];
    for (const p of this.map.places) {
      const n = p.n.toLowerCase();
      const named = n.includes(q);
      if (!named && !(KIND[p.k] || '').toLowerCase().startsWith(q)) continue;
      const rank = LABELS.findIndex((l) => l[0] === p.k);
      matches.push({ p, score: (named ? 0 : 1000) + (n === q ? -100 : 0) + (rank < 0 ? 99 : rank), far: Math.hypot(p.x - this.me.x, p.z - this.me.z) });
    }
    matches.sort((a, b) => a.score - b.score || a.far - b.far);
    this.found = matches.slice(0, 8).map((m) => m.p);
    for (const p of this.found) {
      const km = Math.hypot(p.x - this.me.x, p.z - this.me.z) / 1000;
      this.results.append(el('li', { role: 'option', tabindex: 0, onclick: () => this.pick(p), onkeydown: (e) => e.key === 'Enter' && this.pick(p) }, el('b', null, p.n), el('span', null, `${KIND[p.k] || 'Place'} · ${km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`} away`)));
    }
    if (this.found.length === 0) this.results.append(el('li.none', null, 'Nothing on the map has that name.'));
  }

  // A tap on the map: a card names the place and offers to go there.
  offer(place) {
    clear(this.pickCard);
    const km = Math.hypot(place.x - this.me.x, place.z - this.me.z) / 1000;
    this.pickCard.append(
      el('div.pick-title', null, place.n),
      el('div.pick-meta', null, `${KIND[place.k] || t('Place')} · ${km < 1 ? t('{n} m', { n: Math.round(km * 1000) }) : t('{n} km', { n: km.toFixed(1) })}`),
      el('div.pick-actions', null, button(t('Go here'), { kind: 'primary', size: 'sm', icon: 'flag', onclick: () => this.pick(place) }), button('', { kind: 'ghost', size: 'sm', icon: 'close', ariaLabel: t('Close'), onclick: () => { this.pickCard.hidden = true; } }))
    );
    this.pickCard.hidden = false;
    this.offered = place;
    this.draw();
  }

  pick(place) {
    this.pickCard.hidden = true;
    this.onGoal({ n: place.n, x: place.x, z: place.z });
    this.close();
  }

  buildLegend() {
    clear(this.legend);
    this.legend.append(el('h3', null, t('Legend')));
    const list = el('div.legend-list');
    for (const [kind, [colour, letter]] of Object.entries(BLIP)) {
      list.append(el('div.legend-item', null, el('span.legend-dot', { style: `background:${colour}` }, letter), el('span', null, KIND[kind] || kind)));
    }
    list.append(el('div.legend-item', null, el('span.legend-dot', { style: `background:${ROUTE_COLOUR}` }), el('span', null, t('Your route'))));
    list.append(el('div.legend-item', null, el('span.legend-dot', { style: 'background:#e78fb3' }), el('span', null, t('Where you are going'))));
    list.append(el('div.legend-item', null, el('span.legend-dot', { style: 'background:#f2b705' }, '▲'), el('span', null, t('You'))));
    this.legend.append(list);
  }

  toggleLegend(open = this.legend.hidden) {
    this.legend.hidden = !open;
    this.legendButton.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  draw() {
    const c = this.canvas;
    const ratio = Math.min(2, window.devicePixelRatio || 1);
    const w = c.clientWidth;
    const h = c.clientHeight;
    if (c.width !== Math.round(w * ratio) || c.height !== Math.round(h * ratio)) {
      c.width = Math.round(w * ratio);
      c.height = Math.round(h * ratio);
    }
    const g = this.ctx;
    g.setTransform(ratio, 0, 0, ratio, 0, 0);
    g.fillStyle = '#1b1e23';
    g.fillRect(0, 0, w, h);
    const X = (x) => (x - this.cx) * this.zoom + w / 2;
    const Y = (z) => (z - this.cz) * this.zoom + h / 2;
    const b = this.map.meta.bounds;
    if (this.picture) {
      g.imageSmoothingEnabled = this.zoom < 0.2;
      g.drawImage(this.picture, X(b.x0), Y(b.z0), (b.x1 - b.x0) * this.zoom, (b.z1 - b.z0) * this.zoom);
    } else {
      g.fillStyle = '#2c4a37';
      g.fillRect(X(b.x0), Y(b.z0), (b.x1 - b.x0) * this.zoom, (b.z1 - b.z0) * this.zoom);
    }

    // close up, the streets themselves from the corner map's tiles
    if (this.minimap && this.zoom >= 0.35) {
      g.imageSmoothingEnabled = true;
      for (const tile of this.minimap.tiles.values()) {
        const sx = X(tile.tx * TILE);
        const sy = Y(tile.tz * TILE);
        const size = TILE * this.zoom;
        if (sx > w || sy > h || sx + size < 0 || sy + size < 0) continue;
        g.drawImage(tile.canvas, sx, sy, size + 0.6, size + 0.6);
      }
    }
    // the route by road
    if (this.route && this.route.length > 1) {
      g.lineCap = 'round';
      g.lineJoin = 'round';
      g.beginPath();
      this.route.forEach(([rx, rz], i) => (i ? g.lineTo(X(rx), Y(rz)) : g.moveTo(X(rx), Y(rz))));
      g.strokeStyle = 'rgba(20,23,28,0.75)';
      g.lineWidth = 7;
      g.stroke();
      g.strokeStyle = ROUTE_COLOUR;
      g.lineWidth = 4;
      g.stroke();
    }
    // place markers with their letters, when close enough for them to mean something
    if (this.zoom >= 0.2) {
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      for (const p of this.map.places) {
        const blip = BLIP[p.k];
        if (!blip) continue;
        const x = X(p.x);
        const y = Y(p.z);
        if (x < -20 || y < -20 || x > w + 20 || y > h + 20) continue;
        g.fillStyle = '#14171c';
        g.beginPath();
        g.arc(x, y, 9, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = blip[0];
        g.beginPath();
        g.arc(x, y, 7, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = '#14171c';
        g.font = `700 ${blip[1].length > 1 ? 8 : 10}px "Barlow Semi Condensed", Arial, sans-serif`;
        g.fillText(blip[1], x, y + 0.5);
      }
    }
    // the place tapped
    if (this.offered && !this.pickCard.hidden) {
      g.strokeStyle = '#f7eed9';
      g.lineWidth = 2;
      g.beginPath();
      g.arc(X(this.offered.x), Y(this.offered.z), 13, 0, Math.PI * 2);
      g.stroke();
    }

    // names, the more important ones larger and seen from farther out
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.lineJoin = 'round';
    for (const p of this.map.places) {
      const at = SHOWN.get(p.k);
      if (at === undefined || this.zoom < at) continue;
      const x = X(p.x);
      const y = Y(p.z);
      if (x < -80 || y < -20 || x > w + 80 || y > h + 20) continue;
      const big = p.k === 'town';
      const settlement = big || p.k === 'village' || p.k === 'quarter';
      g.font = `${big ? 700 : 600} ${big ? 18 : settlement ? 14 : 12}px "Barlow Semi Condensed", Arial, sans-serif`;
      g.lineWidth = 3.5;
      g.strokeStyle = 'rgba(20,23,28,0.9)';
      g.strokeText(p.n, x, y);
      g.fillStyle = settlement ? '#f7eed9' : '#f2d27a';
      g.fillText(p.n, x, y);
    }

    // where to go
    for (const m of this.marks) {
      g.fillStyle = '#14171c';
      g.beginPath();
      g.arc(X(m.x), Y(m.z), 10, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = m.color;
      g.beginPath();
      g.arc(X(m.x), Y(m.z), 7.5, 0, Math.PI * 2);
      g.fill();
    }

    // the player: an arrow pointing the way they face (north is up on this map)
    g.save();
    g.translate(X(this.me.x), Y(this.me.z));
    g.rotate(Math.PI - this.me.yaw);
    g.fillStyle = '#14171c';
    g.beginPath();
    g.moveTo(0, -15);
    g.lineTo(10, 11);
    g.lineTo(0, 5);
    g.lineTo(-10, 11);
    g.closePath();
    g.fill();
    g.fillStyle = '#f2b705';
    g.beginPath();
    g.moveTo(0, -11);
    g.lineTo(7, 8);
    g.lineTo(0, 3.5);
    g.lineTo(-7, 8);
    g.closePath();
    g.fill();
    g.restore();

    // a scale bar
    const metres = [100, 200, 500, 1000, 2000, 5000, 10000].find((m) => m * this.zoom > 70) || 20000;
    g.fillStyle = '#f7eed9';
    g.fillRect(16, h - 22, metres * this.zoom, 3);
    g.font = '600 13px "Barlow Semi Condensed", Arial, sans-serif';
    g.textAlign = 'left';
    g.fillText(metres >= 1000 ? `${metres / 1000} km` : `${metres} m`, 16, h - 32);
  }
}
